import {useEffect, useState} from "react";
import {Box, Button, Dialog, IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, Refresh01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {CampaignRepository} from "../../../../../repositories/CampaignRepository";
import {CallStatusChipNew} from "../CampaignStatusChipsNew";
import {ConversationLink} from "../../../../project/campaigns/components/campaign-details/ConversationLink";
import {ACTIVE_POLL_INTERVAL, callOutcomeLabels} from "../../../../project/campaigns/constants";
import {formatDateTime} from "../../../../project/campaigns/utils";
import {usePolling} from "../../../../project/campaigns/hooks/usePolling";

const headerCellSx = {
  fontWeight: 700, fontSize: 11, py: 1, px: 1.5,
  color: "#8A8AA0", textTransform: "uppercase", letterSpacing: "0.06em",
  bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {fontSize: 13, color: "#23264D", whiteSpace: "nowrap", borderBottom: "1px solid #F2F2F7"};
const columns = ["#", "Status", "Outcome", "Called at", "Answered at", "Conversation", "From", "Extension", "Error"];

const SkeletonRow = () => (
  <TableRow>
    {columns.map(column => (
      <TableCell key={column} sx={{border: "none", py: 1.25}}><Skeleton height={16} sx={{borderRadius: "6px"}}/></TableCell>
    ))}
  </TableRow>
);

// Call history of one campaign phone number, newest first
export const CallAttemptsDialogNew = ({campaignId, phoneNumber, projectId, onClose}) => {
  const open = !!phoneNumber;
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAttempts = () => {
    if (!phoneNumber) return;
    setLoading(true);
    CampaignRepository.getAttempts(campaignId, phoneNumber.id)
      .then(response => setAttempts(response?.data || []))
      .catch(() => toast.error("Failed to load call attempts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setAttempts([]);
    fetchAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber?.id]);

  usePolling(fetchAttempts, open && attempts.some(a => a.status === "ACTIVE"), ACTIVE_POLL_INTERVAL);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{sx: {borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)"}}}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{p: 3, pb: 2}}>
        <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
          Call attempts – {phoneNumber?.phoneNumber}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{color: "#8A8AA0"}}>
          <HugeiconsIcon icon={Cancel01Icon} size={18}/>
        </IconButton>
      </Stack>
      <Box sx={{px: 3, overflow: "auto"}}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(column => <TableCell key={column} sx={headerCellSx}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && attempts.length === 0
              ? Array.from({length: 4}).map((_, index) => <SkeletonRow key={index}/>)
              : attempts.map(attempt => (
                  <TableRow key={attempt.id}>
                    <TableCell sx={cellSx}>{attempt.attemptNumber}</TableCell>
                    <TableCell sx={{borderBottom: "1px solid #F2F2F7"}}><CallStatusChipNew status={attempt.status}/></TableCell>
                    <TableCell sx={{...cellSx, color: "#6B6B80"}}>
                      {attempt.callOutcome ? callOutcomeLabels[attempt.callOutcome] || attempt.callOutcome : "-"}
                    </TableCell>
                    <TableCell sx={{...cellSx, color: "#6B6B80"}}>{formatDateTime(attempt.dateCreated)}</TableCell>
                    <TableCell sx={{...cellSx, color: "#6B6B80"}}>{formatDateTime(attempt.answeredAt)}</TableCell>
                    <TableCell sx={cellSx}>
                      <ConversationLink projectId={projectId} conversationId={attempt.conversationId}/>
                    </TableCell>
                    <TableCell sx={{...cellSx, color: "#6B6B80"}}>{attempt.fromNumber || "-"}</TableCell>
                    <TableCell sx={{...cellSx, color: "#6B6B80"}}>{attempt.extension || "-"}</TableCell>
                    <TableCell sx={{borderBottom: "1px solid #F2F2F7"}}>
                      <Typography sx={{fontSize: 12, color: "#DC2626"}}>{attempt.errorMessage || ""}</Typography>
                    </TableCell>
                  </TableRow>
                ))
            }
            {!loading && attempts.length === 0 &&
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{py: 4, color: "#8A8AA0", border: "none"}}>
                  This number hasn't been called yet
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </Box>
      <Stack direction="row" gap={1.5} justifyContent="flex-end" sx={{p: 3, pt: 2}}>
        <Button
          onClick={fetchAttempts}
          startIcon={<HugeiconsIcon icon={Refresh01Icon} size={15}/>}
          sx={{textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"}}}
        >
          Refresh
        </Button>
        <Button
          onClick={onClose}
          sx={{textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px", color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"}}}
        >
          Close
        </Button>
      </Stack>
    </Dialog>
  )
}
